// EXPERIMENT: constituent_psf_experiment (remove with experiment cleanup)
import type React from 'react';

import { Modal, ModalBody } from '@change/ds-core-ui/Modal';
import { NavButton } from '@change/ds-core-ui/NavButton';

import { useI18n } from '@change-corgi/core-react/i18n';

import type { useShareActions } from '#src/app/pages/psf/promoteOrShare/shared/hooks';
import { LocalSignerShare } from '#src/app/shared/components/localSignerShare';

import { useLocalSignerShareActions } from './useLocalSignerShareActions';

type ButtonConfig = ReturnType<typeof useShareActions>['data']['buttonConfigs'][number];

type Props = Readonly<{
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	buttonConfigs: readonly ButtonConfig[];
	/** The sheet's Continue: tracks `combo_psf_share_slide_up_continue` and goes to the share page. */
	onContinue: () => void;
	location: string | undefined;
	isFirstLocalSigner: boolean;
	otherSignerCount: number;
}>;

const Body: React.FC<Omit<Props, 'isOpen' | 'onOpenChange'> & Readonly<{ close: () => void }>> = ({
	buttonConfigs,
	close,
	onContinue,
	location,
	isFirstLocalSigner,
	otherSignerCount,
}) => {
	const { translate } = useI18n();
	const shareActions = useLocalSignerShareActions(buttonConfigs, close);
	return (
		<div className="relative w-full">
			{/* The DS draws its close button only inside `ModalHeader`, which would sit above the gradient;
			    Figma puts the X on the gradient, so the sheet draws the DS button itself. */}
			<NavButton
				variant="close"
				aria-label={translate('corgi.components.modal.close_button')}
				onClick={close}
				className="text-icon-weak absolute top-4 right-4 z-10 p-0"
				data-qa="local-signer-share-close"
			/>
			<LocalSignerShare
				hypothesis="motivation"
				location={location}
				isFirstLocalSigner={isFirstLocalSigner}
				signerCount={otherSignerCount}
				shareActions={shareActions}
				onContinue={onContinue}
				inSheet
			/>
		</div>
	);
};

// The DS sheet pads its content (`px-5 pt-6 pb-[calc(2rem+…)]`, `md:` 6/6/1.5rem) with no prop to turn it
// off, so the body cancels that padding to let the screen's gradient fill the sheet edge to edge, as in
// Figma. The values mirror ds-core-ui's `ModalSheetView`; update them if the DS padding changes.
const FULL_BLEED_BODY =
	'p-0 -mx-5 -mt-6 -mb-[calc(2rem+var(--sheet-scroll-padding-y))] md:-mx-6 md:-mt-6 md:-mb-[calc(1.5rem+var(--sheet-scroll-padding-y))]';

/**
 * Constituents-in-PSF (CHANGE-100768) `v2`: the "Share petition" sheet opened on the new local-signer
 * share screen (Figma "Share - Hypothesis 1") instead of the channel grid. Same sheet, same open/close
 * events and the same Continue as the current sheet, so only the content differs. Closing it (the X,
 * a swipe down or a tap outside) reaches `onOpenChange(false)`, which tracks the close.
 */
export const LocalSignerShareSlideUp: React.FC<Props> = ({ isOpen, onOpenChange, ...bodyProps }) => {
	const { translate } = useI18n();
	return (
		<Modal
			isOpen={isOpen}
			transformToSheet
			onOpenChange={onOpenChange}
			aria-label={translate('corgi.psf.promote_or_share.share_slide_up.aria_label')}
			className="z-100"
		>
			<ModalBody className={FULL_BLEED_BODY}>{({ close }) => <Body {...bodyProps} close={close} />}</ModalBody>
		</Modal>
	);
};
